import { Button, ButtonGroup, Classes, Dialog, Divider, FormGroup, ITreeNode, NonIdealState, Tree } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { IFieldProps } from "decorator-form";
import omit from "lodash/omit";
import React from "react";

import { registerField } from "common/form/register";
import DropdownSelect from "common/form/widgets/common/DropdownSelect";
import NumberWidget from "common/form/widgets/NumberWidget";
import TextWidget from "common/form/widgets/TextWidget";
import IOption from "common/models/IOption";
import { arrayMove } from "core/util/ArrayUtils";
import StringUtil from "core/util/StringUtil";
import { ILayout, ILayoutType, LayoutTypeRegistry } from "./ILayoutType";


/**
 * edit layout configuration data {@link ./ILayoutType.ts}
 *
 * @author chills
 */

interface ILayoutFieldOptions<T> {
	renderRegionInput: (regionData: T, onChange: (regionData: T) => void) => JSX.Element;
	layoutTypes: ILayoutType[];
	useDialog?: boolean;
}

type LayoutFieldProps<T> = IFieldProps<ILayout<T>> & ILayoutFieldOptions<T>;

interface ILayoutFieldState {
	isOpen: boolean;
	active: {
		type: "region" | "layout" | 'breakpoint',
		uuid: any;
	} | null;
}

const dialogStyle = {
	maxWidth: "1000px",
	minWidth: "500px",
	width: '100%'
};

export default class LayoutField<T> extends React.Component<LayoutFieldProps<T>, ILayoutFieldState> {

	public static withOptions<T>(options: ILayoutFieldOptions<T>) {
		return (props: IFieldProps<ILayout<T>>) => <LayoutField {...props} {...options} />;
	}

	public static registered<T>(options: ILayoutFieldOptions<T>) {
		return registerField(this.withOptions(options), "object");
	}

	public state: ILayoutFieldState = {
		active: null,
		isOpen: false
	};

	public render() {
		const { formData = {} as ILayout, schema = {}, useDialog = true } = this.props;

		return (
			<FormGroup label={schema.title} helperText={schema.description}>
				{
					useDialog ? (
						<React.Fragment>
							<Button text="Edit Layout" icon={IconNames.LAYOUT} onClick={this.toggleDialog} />
							{LayoutTypeRegistry.renderDefaultPreview(formData)}
							<Dialog className={Classes.DARK} isOpen={this.state.isOpen} onClose={this.toggleDialog} title="Edit Layout" style={dialogStyle}>
								<div className={Classes.DIALOG_BODY} style={{minHeight: 400}}>
									{this.renderEditor()}
								</div>
							</Dialog>
						</React.Fragment>
					) : this.renderEditor()
				}
			</FormGroup>
		);
	}

	private toggleDialog = () => this.setState({isOpen: !this.state.isOpen});

	private renderEditor = () => {
		const { formData = {} as ILayout<T> } = this.props;
		const { active } = this.state;

		return (
			<div style={{display: 'flex'}}>
				<div style={{maxWidth: "50%", width: "100%", paddingRight: "15px"}}>
					<FormGroup label="Regions" labelInfo="(order does not matter)" helperText="define available regions and what should be rendered into each one.">
						{
							(formData.regions && Object.keys(formData.regions).length > 0) && (
								<FormGroup>
									<Tree
										contents={Object.entries(formData.regions).map<ITreeNode<string>>(([id, region]) => ({
												id,
												nodeData: id,
												label: region.__editName || "Untitled Region",
												isSelected: !!active && active.type === 'region' && active.uuid === id,
												secondaryLabel: (
													<ButtonGroup onClick={(e) => e.stopPropagation()}>
														<Button small={true} minimal={true} icon={IconNames.SMALL_CROSS} onClick={this.removeRegion(id)} />
													</ButtonGroup>
												)
											}))
										}
										onNodeClick={({nodeData}) => this.setActive('region', nodeData)}
									/>
								</FormGroup>
							)
						}
						<FormGroup>
							<Button text="Add Region" onClick={this.addRegion} icon={IconNames.ADD} className={Classes.POPOVER_DISMISS_OVERRIDE} />
						</FormGroup>
					</FormGroup>
					<FormGroup>
						<Divider />
					</FormGroup>
					<FormGroup label="Layouts" helperText="define layout options (add multiple for responsive).">
						{
							(formData.layouts && Object.keys(formData.layouts).length > 0) && (
								<FormGroup>
									<Tree
										contents={
											Object.entries(formData.layouts).map<ITreeNode<string>>(([id, layout]) => ({
												id,
												nodeData: id,
												label: layout.__editName || "Untitled Layout",
												isSelected: !!active && active.type === 'layout' && active.uuid === id,
												secondaryLabel: (
													<ButtonGroup onClick={(e) => e.stopPropagation()}>
														<Button small={true} minimal={true} icon={IconNames.SMALL_CROSS} onClick={this.removeLayout(id)} />
													</ButtonGroup>
												)
											}))
										}
										onNodeClick={({nodeData}) => this.setActive('layout', nodeData)}
									/>
								</FormGroup>
							)
						}
						<FormGroup>
							<Button text="Add Layout" onClick={this.addLayout} icon={IconNames.ADD} className={Classes.POPOVER_DISMISS_OVERRIDE} />
						</FormGroup>
					</FormGroup>
					{
						Object.keys(formData.layouts || {}).length > 1 && (
							<React.Fragment>
								<FormGroup>
									<Divider />
								</FormGroup>
								<FormGroup label="Responsive" helperText="configure rules for how different layouts should be selected based on container size.">
									{
										formData.overrides && (
											<FormGroup>
												<Tree
													contents={
														formData.overrides.map((o, i, arr) => ({
															id: i,
															isSelected: !!active && active.type === 'breakpoint' && active.uuid === i,
															label: o.breakpoint + " - " + (o.layoutKey && formData.layouts[o.layoutKey] && formData.layouts[o.layoutKey].__editName || "No Layout Selected"),
															nodeData: i,
															secondaryLabel: (
																<ButtonGroup minimal={true} onClick={(e) => e.stopPropagation()}>
																	<Button small={true} icon={IconNames.CHEVRON_UP} disabled={i < 1} onClick={this.onBreakpointSort(i, i - 1)} />
																	<Button small={true} icon={IconNames.CHEVRON_DOWN} disabled={i >= arr.length - 1} onClick={this.onBreakpointSort(i, i + 1)} />
																	<Button small={true} icon={IconNames.SMALL_CROSS} onClick={this.removeBreakpoint(i)} />
																</ButtonGroup>
															)
														}))
													}
													onNodeClick={({nodeData}) => this.setActive('breakpoint', nodeData)}
												/>
											</FormGroup>
										)
									}
									<FormGroup>
										<Button text="Add Breakpoint" icon={IconNames.ADD} onClick={this.addBreakpoint} className={Classes.POPOVER_DISMISS_OVERRIDE} />
									</FormGroup>
								</FormGroup>
							</React.Fragment>
						)
					}
				</div>
				<Divider />
				<div style={{maxWidth: "50%", width: "100%", paddingLeft: "15px"}}>
					{this.renderActiveEdit()}
				</div>
			</div>
		);
	}

	private renderActiveEdit = () => {
		const { active } = this.state;

		if (!active) {
			return <NonIdealState title="Select to Edit" description="Choose a region or layout" />;
		}

		const { formData = {} as ILayout<T>, layoutTypes, renderRegionInput } = this.props;

		if (active.type === 'region') {
			const sRegion = formData.regions && formData.regions[active.uuid] || null;
			if (!sRegion) { return null; }

			return (
				<div key={'edit-region-' + active.uuid}>
					<FormGroup label="Region Name">
						<TextWidget value={sRegion.__editName} onChange={this.onRegionChange(active.uuid, '__editName')} />
					</FormGroup>
					<FormGroup label="Region Data">
						{renderRegionInput(sRegion.data, this.onRegionChange(active.uuid, 'data'))}
					</FormGroup>
				</div>
			);
		}

		if (active.type === 'layout') {
			const sLayout = formData.layouts && formData.layouts[active.uuid] || null;
			if (!sLayout) { return null; }

			const layoutType = sLayout.type && LayoutTypeRegistry.getType(sLayout.type) || null;


			return (
				<div key={'edit-layout-' + active.uuid}>
					{
						layoutType && layoutType.renderPreview && (
							<FormGroup label="Preview">
								{layoutType.renderPreview(sLayout.config, formData.regions || {})}
							</FormGroup>
						)
					}
					<FormGroup label="Layout Name">
						<TextWidget value={sLayout.__editName} onChange={this.onLayoutChange(active.uuid, '__editName')} />
					</FormGroup>
					<FormGroup label="Layout Type">
						<DropdownSelect
							onChange={this.onLayoutChange(active.uuid, 'type')}
							value={sLayout.type}
							options={
								layoutTypes.map<IOption<string>>((lt) => ({
									value: lt.type,
									icon: lt.icon,
									label: lt.label
								}))
							}
						/>
					</FormGroup>
					{
						layoutType && (
							<FormGroup>
								{layoutType.renderInput(sLayout.config, this.onLayoutChange(active.uuid, 'config'), formData.regions)}
							</FormGroup>
						)
					}
				</div>
			);
		}

		if (active.type === 'breakpoint') {
			const sBreakpoint = formData.overrides && formData.overrides[active.uuid];
			if (!sBreakpoint) { return null; }

			return (
				<div key={'edit-breakpoint-' + active.uuid}>
					<FormGroup label="Breakpoint">
						<NumberWidget value={sBreakpoint.breakpoint} onChange={this.onBreakpointChange(active.uuid, 'breakpoint')} />
					</FormGroup>
					<FormGroup label="Layout">
						<DropdownSelect
							value={sBreakpoint.layoutKey}
							onChange={this.onBreakpointChange(active.uuid, 'layoutKey')}
							options={Object.entries(formData.layouts || {}).map(([lid, ld]) => ({
								value: lid,
								label: ld.__editName
							}))}
						/>
					</FormGroup>
				</div>
			);
		}

		return null;
	}

	private setActive = (type: 'region' | 'layout' | 'breakpoint', uuid: any) => {
		this.setState({
			active: (this.state.active && this.state.active.type === type && this.state.active.uuid === uuid) ? null : {type, uuid}
		});
	}

	/**
	 * Regions
	 */

	private addRegion = () => {
		const { formData = {} as ILayout<T>, onChange } = this.props;
		const uuid = StringUtil.generateUuid();
		onChange({ ...formData, regions: { ...formData.regions || {}, [uuid]: { __editName: "Untitled Region", data: {} as T } } });
	}

	private removeRegion = (uuid: string) => () => {
		const { formData = {} as ILayout<T>, onChange } = this.props;
		onChange({ ...formData, regions: omit(formData.regions, [uuid]) });
	}

	private onRegionChange = (uuid: string, field: '__editName' | 'data') => (value: any) => {
		const { formData = {} as ILayout<T>, onChange } = this.props;
		onChange({
			...formData,
			regions: {
				...formData.regions,
				[uuid]: {
					...formData.regions[uuid],
					[field]: value
				}
			}
		});
	}

	/**
	 * Layouts
	 */

	private addLayout = () => {
		const { formData = {} as ILayout<T>, onChange } = this.props;
		const uuid = StringUtil.generateUuid();
		onChange({
			...formData,
			layouts: { ...formData.layouts || {}, [uuid]: { __editName: "Untitled Layout", config: {}, type: undefined } },
			layoutKey: !formData.layoutKey ? uuid : formData.layoutKey // set this one as default if no default available yet
		});
	}

	private removeLayout = (uuid: string) => () => {
		const { formData = {} as ILayout<T>, onChange } = this.props;
		onChange({ ...formData, layouts: omit(formData.layouts, [uuid]) });
	}

	private onLayoutChange = (uuid: string, field: 'type' | '__editName' | 'config') => (value: any) => {
		const { formData = {} as ILayout<T>, onChange } = this.props;
		if (field === 'type') {
			onChange({
				...formData,
				layouts: {
					...formData.layouts,
					[uuid]: { ...formData.layouts[uuid], type: value, config: {} } // reset layout config if type changes
				}
			});
		} else {
			onChange({
				...formData,
				layouts: {
					...formData.layouts,
					[uuid]: {
						...formData.layouts[uuid],
						[field]: value
					}
				}
			});
		}
	}

	/**
	 * BREAKPOINTS
	 */

	private addBreakpoint = () => {
		const { formData = {} as ILayout<T>, onChange } = this.props;
		const newBreakpoint = {breakpoint: 768, layoutKey: undefined};
		onChange({
			...formData,
			overrides: formData.overrides ? [...formData.overrides, newBreakpoint] : [newBreakpoint]
		});
	}

	private removeBreakpoint = (index: number) => () => {
		const { formData = {} as ILayout<T>, onChange } = this.props;
		onChange({
			...formData,
			overrides: formData.overrides.filter((_, i) => i !== index)
		});
	}

	private onBreakpointSort = (index: number, targetIndex: number) => () => {
		const { formData = {} as ILayout<T>, onChange } = this.props;
		this.setState({active: null}, () => onChange({
			...formData,
			overrides: arrayMove([...formData.overrides], index, targetIndex)
		}));
	}

	private onBreakpointChange = (index: number, field: 'breakpoint' | 'layoutKey') => (value: any) => {
		const { formData = {} as ILayout<T>, onChange } = this.props;
		onChange({
			...formData,
			overrides: formData.overrides.map(
				(o, i) => i === index ? { ...o, [field]: value } : o
			)
		});
	}
}



// WEBPACK FOOTER //
// ./src/common/form/fields/layout/LayoutField.tsx